// useChatScroll.js
import {nextTick, watch} from "vue";
import {debounce} from "lodash-es";

export function useChatScroll({messagesContainer, chatStore, currentUser, headerHeight, isLoading, observeMessages}) {

    let isInitialScrollDone = false;
    let lastChatId = null;

    const waitForMessages = () => {
        return new Promise((resolve) => {
            const observer = new MutationObserver((mutations, obs) => {
                const messages = messagesContainer.value?.querySelectorAll('.message');
                if (messages && messages.length > 0) {
                    obs.disconnect();
                    resolve(messages);
                }
            });

            if (messagesContainer.value) {
                observer.observe(messagesContainer.value, {
                    childList: true,
                    subtree: true
                });
            }

            const existingMessages = messagesContainer.value?.querySelectorAll('.message');
            if (existingMessages && existingMessages.length > 0) {
                observer.disconnect();
                resolve(existingMessages);
            }
        });
    };

    const scrollToFirstUnread = async (lastReadAt) => {
        try {
            // Ждем появления сообщений в DOM
            const messageElements = await waitForMessages();

            // Если нет даты последнего прочитанного, загружаем историю пока не найдём непрочитанное
            if (!lastReadAt) {
                await loadUntilFirstUnread();
                return;
            }

            let firstUnreadIndex = -1;
            let newestUnreadMessageId = null;

            messageElements.forEach((msg, index) => {
                const messageDate = msg.dataset.messageCreated_at;
                const messageId = msg.dataset.messageId;
                if (firstUnreadIndex === -1 && (!lastReadAt || new Date(messageDate) >= new Date(lastReadAt))) {
                    firstUnreadIndex = index;
                    newestUnreadMessageId = messageId;
                }
            });

            if (firstUnreadIndex !== -1) {
                const targetMessage = messageElements[firstUnreadIndex];
                const container = messagesContainer.value;

                // Рассчитываем позицию с учетом высоты шапки
                const messageTop = targetMessage.offsetTop;
                const messageHeight = targetMessage.offsetHeight;
                const containerHeight = container.clientHeight;
                const headerOffset = headerHeight.value; // Учитываем высоту шапки

                // Прокручиваем с учетом отступа
                container.scrollTo({
                    top: messageTop - (containerHeight / 2) + (messageHeight / 2) + headerOffset,
                    behavior: 'auto'
                });

                // Помечаем сообщение как прочитанное по ID
                if (newestUnreadMessageId) {
                    await chatStore.markMessagesAsRead(newestUnreadMessageId);
                }
            } else {
                // Прокручиваем вниз с учетом высоты шапки
                if (messagesContainer.value) {
                    messagesContainer.value.scrollTo({
                        top: messagesContainer.value.scrollHeight,
                        behavior: 'auto'
                    });
                }
            }
        } catch (error) {
            console.error('Error scrolling:', error);
            if (messagesContainer.value) {
                messagesContainer.value.scrollTo({
                    top: messagesContainer.value.scrollHeight,
                    behavior: 'auto'
                });
            }
        }
    };

    const loadUntilFirstUnread = async () => {
        if (!chatStore.currentChat?.id) {
            console.warn('Чат не выбран, пропускаем loadUntilFirstUnread');
            return;
        }

        let foundUnread = false;
        let attempts = 0;
        const maxAttempts = 10;

        while (!foundUnread && attempts < maxAttempts && !chatStore.allLoaded) {
            attempts++;

            await chatStore.fetchMessages(chatStore.currentChat.id);
            await nextTick();

            const messageElements = messagesContainer.value?.querySelectorAll('.message');
            if (messageElements) {
                for (const el of messageElements) {
                    if (el.classList.contains('unread-message')) {
                        foundUnread = true;
                        // Прокручиваем к первому непрочитанному
                        el.scrollIntoView({behavior: 'auto', block: 'center'});

                        // Помечаем как прочитанное по ID
                        const messageId = el.dataset.messageId;
                        if (messageId) {
                            await chatStore.markMessagesAsRead(messageId);
                        }
                        break;
                    }
                }
            }

            if (!foundUnread && !chatStore.allLoaded) {
                await new Promise(resolve => setTimeout(resolve, 300));
            }
        }

        // Если так и не нашли непрочитанные - скроллим вниз
        if (!foundUnread) {
            if (messagesContainer.value) {
                messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;

                // Если есть сообщения - отмечаем последнее как прочитанное
                if (chatStore.messages.length > 0) {
                    const lastMessage = chatStore.messages[chatStore.messages.length - 1];
                    await chatStore.markMessagesAsRead(lastMessage.id);
                }
            }
        }
    };

    const handleScroll = debounce(async () => {
        const container = messagesContainer.value;
        if (!container) return;

        // Сохраняем текущую позицию прокрутки перед загрузкой
        const scrollTopBefore = container.scrollTop;
        const scrollHeightBefore = container.scrollHeight;

        // Проверяем, находится ли пользователь внизу
        const isAtBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 10;

        if (isAtBottom && chatStore.messages.length > 0) {
            const lastMessage = chatStore.messages[chatStore.messages.length - 1];
            if (!lastMessage.is_read && lastMessage.sender_id !== currentUser.value?.id) {
                await chatStore.markMessagesAsRead(lastMessage.id);
            }
        }

        // Загрузка старых сообщений при достижении верха (порог 50px)
        if (container.scrollTop < 50 && !chatStore.loading && !chatStore.allLoaded) {
            try {
                await chatStore.fetchMessages(chatStore.currentChat.id);

                // После загрузки восстанавливаем позицию прокрутки
                await nextTick();
                const scrollHeightDiff = container.scrollHeight - scrollHeightBefore;
                container.scrollTop = scrollTopBefore + scrollHeightDiff;
            } catch (error) {
                console.error('Error handleScroll:', error);
            }
        }
    }, 100, {leading: true, trailing: true});

    // Добавляем принудительное обновление после загрузки
    watch(() => chatStore.messages, async (newMessages, oldMessages) => {

        // Проверяем, что контейнер существует ДО продолжения
        if (!messagesContainer.value) {
            // Ждём пока контейнер не появится
            await new Promise(resolve => {
                const unwatch = watch(() => messagesContainer.value, (val) => {
                    if (val) {
                        unwatch();
                        resolve();
                    }
                }, { immediate: true });
                
                // Таймаут на случай если что-то пошло не так
                setTimeout(() => {
                    unwatch();
                    resolve();
                }, 1000);
            });
            
            if (!messagesContainer.value) {
                return;
            }
        }

        // Детектируем пагинацию (сообщения добавлены в начало списка)
        const isPagination = oldMessages && oldMessages.length > 0 &&
                             newMessages.length > oldMessages.length &&
                             newMessages[newMessages.length - 1]?.id === oldMessages[oldMessages.length - 1]?.id;

        // Если это пагинация, НЕ скроллим вниз
        if (isPagination) {
            await nextTick();
            observeMessages();
            return;
        }

        // Новое сообщение добавлено в конец (WS или собственная отправка) — всегда скроллим вниз
        const isNewMessageAppended = oldMessages && oldMessages.length > 0 &&
                                     newMessages.length > oldMessages.length &&
                                     newMessages[newMessages.length - 1]?.id !== oldMessages[oldMessages.length - 1]?.id;

        await nextTick();
        await nextTick();
        observeMessages();

        if (isNewMessageAppended) {
            messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
            return;
        }

        // Проверяем, сменился ли чат
        const currentChatId = chatStore.currentChat?.id;
        const isChatChanged = currentChatId !== lastChatId;

        if (isChatChanged) {
            isInitialScrollDone = false;
            lastChatId = currentChatId;
        }

        // Не скроллим снова, если уже всё прогружено и скроллировали для этого чата
        if (isInitialScrollDone && chatStore.allLoaded && !isChatChanged) {
            return;
        }

        const lastRead = chatStore.currentChat?.participant?.last_read_at;

        // Функция для скролла вниз
        const scrollToBottom = () => {
            const container = messagesContainer.value;
            if (!container) return;
            
            // Используем setTimeout для гарантии, что DOM обновился
            setTimeout(() => {
                const lastMessage = container.querySelector('.message:last-child');
                if (lastMessage) {
                    lastMessage.scrollIntoView({ behavior: 'auto', block: 'end' });
                } else {
                    // Фоллбэк, если сообщений нет
                    container.scrollTop = container.scrollHeight;
                }
            }, 100);
        };

        // Если есть непрочитанные сообщения - скроллим к первому непрочитанному
        // Иначе - скроллим вниз к последнему сообщению
        if (lastRead) {
            const hasUnread = chatStore.messages.some(m => !m.is_read && String(m.sender_id) !== String(currentUser.value?.id));
            if (hasUnread) {
                await scrollToFirstUnread(lastRead);
            } else {
                // Нет непрочитанных - скроллим вниз
                scrollToBottom();
            }
        } else {
            // Нет информации о прочтении - скроллим вниз к последнему сообщению
            scrollToBottom();
        }

        isInitialScrollDone = true;
    }, { immediate: true });

    return {
        handleScroll,
        scrollToFirstUnread,
        loadUntilFirstUnread
    };
}
